import React from 'react';
import { Card, Descriptions, Spin, Empty } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getLostAndFoundItemById } from '@/api/lostAndFound';
import { useAuth } from '@/contexts/AuthContext';
import dayjs from 'dayjs';


interface LostAndFoundTabProps {
  itemId: string;
}

const LostAndFoundTab: React.FC<LostAndFoundTabProps> = ({ itemId }) => {
  const { t } = useTranslation(['lostAndFound', 'common']);
  const { firestoreUser } = useAuth();

  const { data: item, isLoading, error } = useQuery({
    queryKey: ['lostAndFoundItem', itemId],
    queryFn: () => getLostAndFoundItemById(itemId, firestoreUser!),
    enabled: !!itemId && !!firestoreUser,
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <Card>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (error || !item) {
    return (
      <Card>
        <Empty description={t('lostAndFound:notFound')} />
      </Card>
    );
  }

  return (
    <Card>
      <Descriptions bordered column={1}>
        <Descriptions.Item label={t('lostAndFound:fields.title')}>
          {item.title}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('lostAndFound:fields.description')}>
          {item.description}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('lostAndFound:fields.category')}>
          {t(`lostAndFound:categories.${item.category}`)}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('lostAndFound:fields.status')}>
          {t(`lostAndFound:status.${item.status}`)}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('lostAndFound:fields.condition')}>
          {t(`lostAndFound:condition.${item.condition}`)}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('lostAndFound:fields.foundAt')}>
          {dayjs(item.foundAt).format('DD/MM/YYYY HH:mm')}
        </Descriptions.Item>
        
        {item.returnedAt && (
          <Descriptions.Item label={t('lostAndFound:fields.returnedAt')}>
            {dayjs(item.returnedAt).format('DD/MM/YYYY HH:mm')}
          </Descriptions.Item>
        )}
        
        {item.reportedBy && (
          <Descriptions.Item label={t('lostAndFound:fields.reportedBy')}>
            {item.reportedBy.name}
          </Descriptions.Item>
        )}
        
        {item.claimedBy && (
          <Descriptions.Item label={t('lostAndFound:fields.claimedBy')}>
            {item.claimedBy.name}
          </Descriptions.Item>
        )}
        
        {item.handedOverTo && (
          <Descriptions.Item label={t('lostAndFound:fields.handedOverTo')}>
            {item.handedOverTo.name}
          </Descriptions.Item>
        )}
        
        {item.block && (
          <Descriptions.Item label={t('lostAndFound:fields.block')}>
            {item.block.name}
          </Descriptions.Item>
        )}
        
        {item.apartmentUnit && (
          <Descriptions.Item label={t('lostAndFound:fields.apartmentUnit')}>
            {item.apartmentUnit.name}
          </Descriptions.Item>
        )}
        
        {item.value && (
          <Descriptions.Item label={t('lostAndFound:fields.value')}>
            {item.value.amount} {item.value.currency}
          </Descriptions.Item>
        )}
        
        {item.photos && item.photos.length > 0 && (
          <Descriptions.Item label={t('lostAndFound:fields.photos')}>
            {item.photos.length} {t('common:items')}
          </Descriptions.Item>
        )}
      </Descriptions>
    </Card>
  );
};

export default LostAndFoundTab; 